import React, { useState, useEffect } from "react";
import styled from "styled-components";
import PrimaryButton from "../Generic/PrimaryButton";
import CustomModal from "../Generic/CustomModal";
import Toggle from "../Generic/Toggle";
import Tooltip from "../Generic/Tooltip";
import StyledTable, { TableBody, TableCell, TableHead, TableHeader, TableRow } from "../Generic/StyledTable";
import WebhookEndpointFormModal from "./WebhookEndpointFormModal";
import WebhookDeliveriesModal from "./WebhookDeliveriesModal";
import { ReactComponent as CopyIcon } from "../../assets/icons/copyIcon.svg";
import { History, Pencil, RefreshCw, Send, Trash2 } from "lucide-react";
import { copyToClipboard } from "../../helper/copy";
import { formatDate } from "../../helper/dateFormat";
import { useProjectContext } from "../../context/useProjectContext";
import { showErrorNotification, showGenericError, showSuccessNotification } from "../../helper/Notifications";
import {
  getWebhookEndpointsAPICall,
  createWebhookEndpointAPICall,
  updateWebhookEndpointAPICall,
  deleteWebhookEndpointAPICall,
  rollWebhookSecretAPICall,
  sendWebhookTestAPICall,
} from "../../api/webhooks/webhookService";

const MAX_ENDPOINTS = 5;

const maskSecret = (secret) => (secret ? `${secret.slice(0, 10)}••••••••${secret.slice(-4)}` : "-");

const asPromise = (call) =>
  new Promise((resolve, reject) =>
    call(resolve, (error) => {
      const message = error?.response?.data?.message;
      message ? showErrorNotification(message) : showGenericError();
      reject(error);
    })
  );

const WebhooksSection = () => {
  const { selectedProject } = useProjectContext();
  const [endpoints, setEndpoints] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [endpointToEdit, setEndpointToEdit] = useState(null);
  const [endpointToDelete, setEndpointToDelete] = useState(null);
  const [endpointToRoll, setEndpointToRoll] = useState(null);
  const [deliveriesEndpoint, setDeliveriesEndpoint] = useState(null);

  const projectId = selectedProject?.id;

  const loadEndpoints = () => {
    getWebhookEndpointsAPICall(
      projectId,
      (response) => {
        setEndpoints(response.data.webhook_endpoints);
        setEventTypes(response.data.event_types);
      },
      showGenericError
    );
  };

  useEffect(() => {
    if (projectId) loadEndpoints();
  }, [projectId]);

  const openForm = (endpoint = null) => {
    setEndpointToEdit(endpoint);
    setShowFormModal(true);
  };

  const closeForm = () => {
    setShowFormModal(false);
    setEndpointToEdit(null);
  };

  const handleSubmit = async (data) => {
    await asPromise((onSuccess, onError) =>
      endpointToEdit
        ? updateWebhookEndpointAPICall(projectId, endpointToEdit.id, data, onSuccess, onError)
        : createWebhookEndpointAPICall(projectId, data, onSuccess, onError)
    );
    loadEndpoints();
  };

  const handleDelete = async () => {
    await asPromise((onSuccess, onError) => deleteWebhookEndpointAPICall(projectId, endpointToDelete.id, onSuccess, onError));
    loadEndpoints();
  };

  const handleRollSecret = async () => {
    await asPromise((onSuccess, onError) => rollWebhookSecretAPICall(projectId, endpointToRoll.id, onSuccess, onError));
    loadEndpoints();
  };

  const handleToggleEnabled = (endpoint) => {
    asPromise((onSuccess, onError) =>
      updateWebhookEndpointAPICall(projectId, endpoint.id, { enabled: !endpoint.enabled }, onSuccess, onError)
    ).then(() => {
      showSuccessNotification(endpoint.enabled ? "Webhook endpoint disabled" : "Webhook endpoint enabled");
      loadEndpoints();
    }, () => {});
  };

  const handleSendTest = (endpoint) => {
    asPromise((onSuccess, onError) => sendWebhookTestAPICall(projectId, endpoint.id, onSuccess, onError))
      .then(() => showSuccessNotification("Test event sent. Check the deliveries for the result."), () => {});
  };

  return (
    <Section>
      <WebhookEndpointFormModal
        isOpen={showFormModal}
        onClose={closeForm}
        endpointToEdit={endpointToEdit}
        eventTypes={eventTypes}
        onSubmit={handleSubmit}
      />

      <WebhookDeliveriesModal
        projectId={projectId}
        endpoint={deliveriesEndpoint}
        onClose={() => setDeliveriesEndpoint(null)}
      />

      <CustomModal
        isOpen={!!endpointToDelete}
        onOpenChange={() => setEndpointToDelete(null)}
        iconType="warning"
        iconAlignment="center"
        title="Delete this webhook endpoint?"
        titleAlignment="center"
        buttonLayout="row"
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
          { text: "Cancel", onClick: () => setEndpointToDelete(null), variant: "secondary" },
          { text: "Delete Endpoint", action: handleDelete, variant: "delete", successMessage: "Webhook endpoint deleted" },
        ]}
      >
        <ModalText>Events will stop being sent to this URL, and its delivery log is removed.</ModalText>
      </CustomModal>

      <CustomModal
        isOpen={!!endpointToRoll}
        onOpenChange={() => setEndpointToRoll(null)}
        iconType="warning"
        iconAlignment="center"
        title="Roll the signing secret?"
        titleAlignment="center"
        buttonLayout="row"
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
          { text: "Cancel", onClick: () => setEndpointToRoll(null), variant: "secondary" },
          { text: "Roll Secret", action: handleRollSecret, variant: "primary", successMessage: "Signing secret rolled" },
        ]}
      >
        <ModalText>
          The current secret stops working immediately. Update your system with the new secret to keep verifying events.
        </ModalText>
      </CustomModal>

      <SectionHeader>
        <div>
          <SectionTitle>Webhooks</SectionTitle>
          <SectionSubtitle>
            Get notified when meetings are created, start, end, or their summary and transcript are ready.
          </SectionSubtitle>
        </div>
        <Tooltip content={`A project can have up to ${MAX_ENDPOINTS} endpoints`} side="top">
          <span>
            <PrimaryButton text="Add Endpoint" onClick={() => openForm()} disabled={endpoints.length >= MAX_ENDPOINTS} />
          </span>
        </Tooltip>
      </SectionHeader>

      <StyledTable>
        <TableHeader>
          <TableRow>
            <TableHead style={{ textAlign: "left" }}>Endpoint URL</TableHead>
            <TableHead style={{ textAlign: "center" }}>Events</TableHead>
            <TableHead style={{ textAlign: "center" }}>Status</TableHead>
            <TableHead style={{ textAlign: "center" }}>Signing Secret</TableHead>
            <TableHead style={{ textAlign: "center", width: "150px" }}>Created On</TableHead>
            <TableHead style={{ textAlign: "center" }}>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {endpoints.length > 0 ? (
            endpoints.map((endpoint) => (
              <TableRow key={endpoint.id}>
                <TableCell>
                  <UrlText data-emotii-exclude>{endpoint.url}</UrlText>
                </TableCell>
                <TableCell style={{ textAlign: "center" }}>
                  {endpoint.event_types.length === 0 ? (
                    <MutedText>All events</MutedText>
                  ) : (
                    <Tooltip content={endpoint.event_types.join(", ")} side="top">
                      <MutedText>{endpoint.event_types.length} selected</MutedText>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell style={{ textAlign: "center" }}>
                  <Tooltip
                    content={endpoint.enabled ? "Disable endpoint" : `Disabled${endpoint.disabled_at ? ` on ${formatDate(endpoint.disabled_at)}` : ""}. Enable to resume deliveries`}
                    side="top"
                  >
                    <StatusCell>
                      <Toggle checked={endpoint.enabled} onCheckedChange={() => handleToggleEnabled(endpoint)} />
                    </StatusCell>
                  </Tooltip>
                </TableCell>
                <TableCell style={{ textAlign: "center" }}>
                  <SecretCell>
                    <SecretText data-emotii-exclude>{maskSecret(endpoint.secret)}</SecretText>
                    <Tooltip content="Copy the signing secret" side="top">
                      <IconButton $outlineIcon onClick={() => copyToClipboard(endpoint.secret)}>
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip content="Roll the signing secret" side="top">
                      <IconButton onClick={() => setEndpointToRoll(endpoint)}>
                        <RefreshCw />
                      </IconButton>
                    </Tooltip>
                  </SecretCell>
                </TableCell>
                <TableCell style={{ textAlign: "center" }}>
                  <MutedText>{formatDate(endpoint.created_at)}</MutedText>
                </TableCell>
                <TableCell>
                  <Actions>
                    <Tooltip content={endpoint.enabled ? "Send a test event" : "Enable the endpoint to send a test event"} side="top">
                      <span>
                        <IconButton onClick={() => handleSendTest(endpoint)} disabled={!endpoint.enabled}>
                          <Send />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip content="View deliveries" side="top">
                      <IconButton onClick={() => setDeliveriesEndpoint(endpoint)}>
                        <History />
                      </IconButton>
                    </Tooltip>
                    <Tooltip content="Edit endpoint" side="top">
                      <IconButton onClick={() => openForm(endpoint)}>
                        <Pencil />
                      </IconButton>
                    </Tooltip>
                    <Tooltip content="Delete endpoint" side="top">
                      <IconButton $danger onClick={() => setEndpointToDelete(endpoint)}>
                        <Trash2 />
                      </IconButton>
                    </Tooltip>
                  </Actions>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6}>
                <EmptyText>Add an endpoint to receive meeting events in your own system.</EmptyText>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </StyledTable>
    </Section>
  );
};

export default WebhooksSection;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
`;

const SectionTitle = styled.h2`
  color: #111827;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  margin: 0;
`;

const SectionSubtitle = styled.p`
  color: #667085;
  font-size: 14px;
  line-height: 20px;
  margin: 0;
`;

const ModalText = styled.p`
  color: #667085;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  margin: 0;
`;

const UrlText = styled.span`
  color: #111827;
  font-size: 14px;
  font-weight: 500;
  display: inline-block;
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
`;

const MutedText = styled.span`
  color: #667085;
  font-size: 14px;
  white-space: nowrap;
`;

const SecretCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const SecretText = styled.span`
  color: #344054;
  font-size: 14px;
  font-family: 'Courier New', monospace;
`;

const StatusCell = styled.span`
  display: inline-flex;
  justify-content: center;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${(props) => (props.$danger ? "#fef2f2" : "#f4ebff")};
    color: ${(props) => (props.$danger ? "#ef4444" : "#7f56d9")};
  }

  &:disabled {
    color: #d0d5dd;
    cursor: default;
  }

  svg {
    width: 16px;
    height: 16px;
    stroke-width: 1.75;
  }

  ${(props) => props.$outlineIcon && `
    svg path {
      fill: none !important;
      stroke: currentColor !important;
      stroke-width: 1.5 !important;
    }
  `}
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

const EmptyText = styled.div`
  color: #667085;
  font-size: 14px;
  text-align: center;
  padding: 40px 20px;
`;
